import { useEffect, useState } from 'react';
import { Box, alpha } from '@mui/material';

type ResizeHandleProps = {
  onResize: (width: number) => void;
  anchor: 'left' | 'right';
  onDragEnd?: () => void;
};

const DrawerResizeHandle = ({ onResize, anchor, onDragEnd }: ResizeHandleProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const isLeft = anchor === 'left';
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const newWidth = isLeft ? e.clientX : window.innerWidth - e.clientX;
    const minWidth = 100;
    const maxWidth = 1000;

    const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    if (onResize) {
      onResize(clampedWidth);
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (onDragEnd) {
        onDragEnd();
      }
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Box
      role="button"
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      sx={(theme) => ({
        flexShrink: 0,
        width: 8,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        cursor: 'col-resize',
        '&::before': {
          content: '""',
          width: '3px',
          borderRadius: '3px',
          backgroundColor: isDragging ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.25),
          boxShadow: isDragging ? `0 0 8px ${alpha(theme.palette.primary.main, 0.55)}` : 'none',
          transition: theme.transitions.create(['background-color', 'box-shadow']),
        },
        '&:hover::before': {
          backgroundColor: theme.palette.primary.main,
          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.55)}`,
        },
      })}
    />
  );
};

export default DrawerResizeHandle;
